import { ContentManifestEntry, getContentManifest } from './contentManifest';
import {
  BiomeContent,
  BossEncounterContent,
  BossEncounterRewardPolicyContent,
  BossEncounterStatModifiersContent,
  BossVictoryRequirementsContent,
  ConsumableContent,
  ContentRegistry,
  EnemyContent,
  EvolutionRecipeContent,
  GameSettingsContent,
  PassiveContent,
  WeaponBehaviorType,
  WaveContent,
  WeaponContent
} from './ContentRegistry';
import { createLogger, Logger } from '../utils/Logger';
import { DataValidator } from '../utils/DataValidator';
import { validateContentVisualReferences } from './assets/visualAssets';

const KNOWN_WAVE_MODIFIER_IDS = new Set(['elite', 'swift', 'fortified', 'berserk']);
const LEGACY_BOSS_ENCOUNTER_ID = 'legacy_boss_1';
const DEFAULT_COMBAT_FEEDBACK = Object.freeze({
  hit: { particleBurstCount: 5, particleIntensity: 1, sound: { key: 'hit', volume: 0.4 } },
  crit: { particleBurstCount: 10, particleIntensity: 1.25, sound: { key: 'crit', volume: 0.5 } },
  burn: { particleBurstCount: 8, particleIntensity: 1.1, sound: { key: 'burn', volume: 0.4 } },
  slow: { particleBurstCount: 8, particleIntensity: 1, sound: { key: 'slow', volume: 0.35 } },
  chain: { particleBurstCount: 12, particleIntensity: 1.2, sound: { key: 'chain', volume: 0.5 } },
  area: { particleBurstCount: 12, particleIntensity: 1.15, sound: { key: 'area_hit', volume: 0.45 } },
  enemyDeath: { particleBurstCount: 14, particleIntensity: 1.1, sound: { key: 'enemy_death', volume: 0.5 } },
  bossDeath: { particleBurstCount: 24, particleIntensity: 1.5, shakeDurationMs: 500, shakeIntensity: 0.02, sound: { key: 'boss_death', volume: 0.7 } },
  playerHit: { tintDurationMs: 100, shakeDurationMs: 200, shakeIntensity: 0.01, sound: { key: 'player_hit', volume: 0.5 } },
  bossWarning: { shakeDurationMs: 1000, shakeIntensity: 0.01, flashDurationMs: 250, flashAlpha: 0.5, sound: { key: 'boss_warning', volume: 0.6 } },
  levelUp: { particleBurstCount: 30, particleIntensity: 1.5, sound: { key: 'levelup', volume: 0.6 } },
  chestReward: { particleBurstCount: 18, particleIntensity: 1.2, sound: { key: 'chest_open', volume: 0.55 } },
  evolutionReveal: { particleBurstCount: 22, particleIntensity: 1.35, sound: { key: 'evolution', volume: 0.65 } }
});

interface ParsedContent {
  weapons: WeaponContent[];
  passives: PassiveContent[];
  evolutionRecipes: EvolutionRecipeContent[];
  consumables: ConsumableContent[];
  enemies: EnemyContent[];
  biomes: BiomeContent[];
  waveTable?: readonly WaveContent[];
  gameSettings?: GameSettingsContent;
}

export function loadContentRegistry(
  manifest: readonly ContentManifestEntry[] = getContentManifest(),
  logger: Logger = createLogger('ContentLoader')
): ContentRegistry {
  logger.debug('loadContentRegistry:start', { entries: manifest.length });

  const validator = new DataValidator();
  const parsed: ParsedContent = {
    weapons: [],
    passives: [],
    evolutionRecipes: [],
    consumables: [],
    enemies: [],
    biomes: []
  };

  for (const entry of manifest) {
    logger.debug('loadContentRegistry:validate-entry:start', {
      type: entry.type,
      id: entry.id,
      schemaId: entry.schemaId,
      path: entry.path,
      required: entry.required
    });

    const validation = validator.validate(entry.schemaId, entry.data);
    if (!validation.isValid) {
      const payload = {
        type: entry.type,
        id: entry.id,
        schemaId: entry.schemaId,
        path: entry.path,
        errors: validation.errors ?? []
      };

      if (entry.required) {
        logger.error('loadContentRegistry:invalid-required-content', payload);
        throw new Error(`Invalid required content: ${entry.path}`);
      }

      logger.warn('loadContentRegistry:invalid-optional-content-skipped', payload);
      continue;
    }

    if (entry.type === 'weapon') {
      assertWeaponConsistency(entry, entry.data as WeaponContent, logger);
    }

    addEntry(parsed, entry);
    logger.debug('loadContentRegistry:validate-entry:complete', {
      type: entry.type,
      id: entry.id,
      path: entry.path
    });
  }

  assertRequiredBuckets(parsed, logger);
  parsed.gameSettings = normalizeGameSettings(parsed.gameSettings as GameSettingsContent, logger);
  logger.debug('loadContentRegistry:settings-summary', {
    maxWeapons: parsed.gameSettings?.maxWeapons,
    maxPassives: parsed.gameSettings?.maxPassives,
    starterWeaponIds: parsed.gameSettings?.starterWeaponIds,
    availableWeaponIds: parsed.gameSettings?.availableWeaponIds,
    consumableFallbackIds: parsed.gameSettings?.consumableFallbackIds,
    bossEnemyId: parsed.gameSettings?.bossEnemyId,
    bossSpawnTime: parsed.gameSettings?.bossSpawnTime,
    bossesToWin: parsed.gameSettings?.bossesToWin,
    bossEncounterCount: parsed.gameSettings?.bossEncounters?.length,
    bossEncounterIds: parsed.gameSettings?.bossEncounters?.map(encounter => encounter.id),
    bossEncounterTimings: parsed.gameSettings?.bossEncounters?.map(encounter => encounter.spawnTime)
  });
  assertCrossReferences(parsed as Required<ParsedContent>, logger);
  assertVisualReferences(parsed as Required<ParsedContent>, logger);

  const registry = new ContentRegistry({
    weapons: parsed.weapons,
    passives: parsed.passives,
    evolutionRecipes: parsed.evolutionRecipes,
    consumables: parsed.consumables,
    enemies: parsed.enemies,
    biomes: parsed.biomes,
    waveTable: parsed.waveTable ?? [],
    gameSettings: parsed.gameSettings as GameSettingsContent
  });

  logger.info('loadContentRegistry:complete', registry.getSummary());
  return registry;
}

function assertVisualReferences(parsed: Required<ParsedContent>, logger: Logger): void {
  const result = validateContentVisualReferences({
    enemies: parsed.enemies,
    weapons: parsed.weapons,
    passives: parsed.passives,
    consumables: parsed.consumables
  }, logger);

  if (!result.ok) {
    throw new Error(`Invalid visual content references: ${result.errors.join('; ')}`);
  }
}

function addEntry(parsed: ParsedContent, entry: ContentManifestEntry): void {
  switch (entry.type) {
    case 'weapon':
      parsed.weapons.push(entry.data as WeaponContent);
      break;
    case 'passive':
      parsed.passives.push(entry.data as PassiveContent);
      break;
    case 'evolution':
      parsed.evolutionRecipes.push(entry.data as EvolutionRecipeContent);
      break;
    case 'consumable':
      parsed.consumables.push(entry.data as ConsumableContent);
      break;
    case 'enemy':
      parsed.enemies.push(entry.data as EnemyContent);
      break;
    case 'biome':
      parsed.biomes.push(entry.data as BiomeContent);
      break;
    case 'wave':
      parsed.waveTable = entry.data as readonly WaveContent[];
      break;
    case 'game':
      parsed.gameSettings = entry.data as GameSettingsContent;
      break;
  }
}

function normalizeGameSettings(settings: GameSettingsContent, logger: Logger): GameSettingsContent {
  logger.debug('normalizeGameSettings:start', {
    bossSpawnTime: settings.bossSpawnTime,
    bossesToWin: settings.bossesToWin,
    bossEnemyId: settings.bossEnemyId,
    hasBossEncounters: !!settings.bossEncounters?.length,
    hasBossVictoryRequirements: !!settings.bossVictoryRequirements
  });

  let bossEncounters = normalizeBossEncounters(settings.bossEncounters, settings, logger);
  const firstRequiredEncounter = bossEncounters.find(encounter => encounter.requiredForVictory !== false) ?? bossEncounters[0];

  if (!firstRequiredEncounter) {
    logger.error('normalizeGameSettings:missing-boss-encounters-after-normalization');
    throw new Error('Invalid game settings: bossEncounters are required after normalization');
  }

  let bossVictoryRequirements = normalizeBossVictoryRequirements(settings, bossEncounters, logger);
  bossVictoryRequirements = normalizeBossVictoryRequirements(
    {
      ...settings,
      bossVictoryRequirements
    },
    bossEncounters,
    logger
  );

  const normalizedSettings: GameSettingsContent = {
    ...settings,
    experience: settings.experience ?? { baseXp: 10, growthFactor: 1.5 },
    bossSpawnTime: firstRequiredEncounter.spawnTime,
    bossesToWin: bossVictoryRequirements.requiredBossCount,
    bossEnemyId: firstRequiredEncounter.enemyId,
    bossEncounters,
    bossVictoryRequirements,
    combatFeedback: normalizeCombatFeedbackSettings(settings.combatFeedback, logger)
  };

  logger.debug('normalizeGameSettings:encounter-summary', {
    firstRequiredEncounterId: firstRequiredEncounter.id,
    derivedBossEnemyId: normalizedSettings.bossEnemyId,
    derivedBossSpawnTime: normalizedSettings.bossSpawnTime,
    derivedBossesToWin: normalizedSettings.bossesToWin,
    encounterCount: bossEncounters.length,
    encounterIds: bossEncounters.map(encounter => encounter.id),
    requiredEncounterIds: bossVictoryRequirements.requiredEncounterIds
  });

  if (!settings.bossEncounters?.length) {
    logger.warn('normalizeGameSettings:legacy-boss-encounter-fallback', {
      legacyBossEnemyId: settings.bossEnemyId,
      legacyBossSpawnTime: settings.bossSpawnTime,
      generatedEncounterId: LEGACY_BOSS_ENCOUNTER_ID
    });
  }

  if (!settings.bossVictoryRequirements) {
    logger.warn('normalizeGameSettings:legacy-victory-requirements-fallback', {
      legacyBossesToWin: settings.bossesToWin,
      requiredEncounterIds: bossVictoryRequirements.requiredEncounterIds
    });
  }

  if (
    settings.bossEnemyId !== normalizedSettings.bossEnemyId
    || settings.bossSpawnTime !== normalizedSettings.bossSpawnTime
    || settings.bossesToWin !== normalizedSettings.bossesToWin
  ) {
    logger.warn('normalizeGameSettings:legacy-fields-overridden-by-encounters', {
      legacyBossEnemyId: settings.bossEnemyId,
      legacyBossSpawnTime: settings.bossSpawnTime,
      legacyBossesToWin: settings.bossesToWin,
      derivedBossEnemyId: normalizedSettings.bossEnemyId,
      derivedBossSpawnTime: normalizedSettings.bossSpawnTime,
      derivedBossesToWin: normalizedSettings.bossesToWin
    });
  }

  logger.debug('normalizeGameSettings:complete');
  return normalizedSettings;
}

function normalizeCombatFeedbackSettings(
  combatFeedback: GameSettingsContent['combatFeedback'] | undefined,
  logger: Logger
): GameSettingsContent['combatFeedback'] {
  const base = (combatFeedback ?? {}) as Record<string, unknown>;
  const normalized: Record<string, unknown> = {};

  Object.entries(DEFAULT_COMBAT_FEEDBACK).forEach(([key, defaults]) => {
    const typedDefaults = defaults as Record<string, unknown>;
    const custom = (base[key] ?? {}) as Record<string, unknown>;
    normalized[key] = {
      ...typedDefaults,
      ...custom,
      particleBurstCount: clampOptionalNumber(custom.particleBurstCount, asNumber(typedDefaults.particleBurstCount), 0, 300, `combatFeedback.${key}.particleBurstCount`, logger),
      particleIntensity: clampOptionalNumber(custom.particleIntensity, asNumber(typedDefaults.particleIntensity), 0, 4, `combatFeedback.${key}.particleIntensity`, logger),
      shakeDurationMs: clampOptionalNumber(custom.shakeDurationMs, asNumber(typedDefaults.shakeDurationMs), 0, 5000, `combatFeedback.${key}.shakeDurationMs`, logger),
      shakeIntensity: clampOptionalNumber(custom.shakeIntensity, asNumber(typedDefaults.shakeIntensity), 0, 0.2, `combatFeedback.${key}.shakeIntensity`, logger),
      flashDurationMs: clampOptionalNumber(custom.flashDurationMs, asNumber(typedDefaults.flashDurationMs), 0, 5000, `combatFeedback.${key}.flashDurationMs`, logger),
      flashAlpha: clampOptionalNumber(custom.flashAlpha, asNumber(typedDefaults.flashAlpha), 0, 1, `combatFeedback.${key}.flashAlpha`, logger),
      tintDurationMs: clampOptionalNumber(custom.tintDurationMs, asNumber(typedDefaults.tintDurationMs), 0, 5000, `combatFeedback.${key}.tintDurationMs`, logger),
      sound: normalizeCombatSoundCue(typedDefaults.sound as { key: string; volume?: number } | undefined, custom.sound, `combatFeedback.${key}.sound`, logger)
    };
  });

  logger.debug('normalizeCombatFeedbackSettings:complete', { keys: Object.keys(normalized) });
  return normalized as GameSettingsContent['combatFeedback'];
}

function normalizeCombatSoundCue(
  defaults: { key: string; volume?: number } | undefined,
  custom: unknown,
  path: string,
  logger: Logger
): { key: string; volume?: number } | undefined {
  if (!defaults && custom === undefined) {
    return undefined;
  }
  const parsed = (custom ?? {}) as Record<string, unknown>;
  const key = typeof parsed.key === 'string' && parsed.key.trim().length > 0
    ? parsed.key
    : defaults?.key;

  if (!key) {
    logger.warn('normalizeCombatFeedbackSettings:missing-sound-key', { path });
    return undefined;
  }

  return {
    key,
    volume: clampOptionalNumber(parsed.volume, defaults?.volume, 0, 1, `${path}.volume`, logger)
  };
}

function clampOptionalNumber(
  value: unknown,
  fallback: number | undefined,
  min: number,
  max: number,
  key: string,
  logger: Logger
): number | undefined {
  if (value === undefined) {
    return fallback;
  }
  if (!Number.isFinite(value)) {
    logger.warn('normalizeCombatFeedbackSettings:non-finite-value', { key, value, fallback });
    return fallback;
  }
  const clamped = Math.min(max, Math.max(min, Number(value)));
  if (clamped !== value) {
    logger.warn('normalizeCombatFeedbackSettings:clamped-value', { key, value, clamped, min, max });
  }
  return clamped;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function normalizeBossEncounters(
  encounters: readonly BossEncounterContent[] | undefined,
  settings: GameSettingsContent,
  logger: Logger
): readonly BossEncounterContent[] {
  if (encounters && encounters.length > 0) {
    return encounters.map(encounter => ({
      ...encounter,
      requiredForVictory: encounter.requiredForVictory !== false
    }));
  }

  logger.warn('normalizeBossEncounters:using-legacy-single-boss-settings', {
    bossEnemyId: settings.bossEnemyId,
    bossSpawnTime: settings.bossSpawnTime
  });

  return [{
    id: LEGACY_BOSS_ENCOUNTER_ID,
    spawnTime: settings.bossSpawnTime,
    enemyId: settings.bossEnemyId,
    hpMultiplier: 1,
    rewardPolicy: {
      mode: 'enemyDrops'
    },
    requiredForVictory: true
  }];
}

function normalizeBossVictoryRequirements(
  settings: GameSettingsContent,
  encounters: readonly BossEncounterContent[],
  logger: Logger
): BossVictoryRequirementsContent {
  if (settings.bossVictoryRequirements) {
    return {
      requiredEncounterIds: [...settings.bossVictoryRequirements.requiredEncounterIds],
      requiredBossCount: settings.bossVictoryRequirements.requiredBossCount
    };
  }

  const requiredEncounterIds = encounters
    .filter(encounter => encounter.requiredForVictory !== false)
    .map(encounter => encounter.id);
  const requiredBossCount = Math.max(1, settings.bossesToWin);

  if (requiredEncounterIds.length === 0) {
    logger.warn('normalizeBossVictoryRequirements:no-required-encounters-defaulting-to-first', {
      firstEncounterId: encounters[0]?.id
    });
    if (encounters[0]) {
      requiredEncounterIds.push(encounters[0].id);
    }
  }

  return {
    requiredEncounterIds: requiredEncounterIds.slice(0, Math.max(requiredBossCount, 1)),
    requiredBossCount
  };
}

function assertRequiredBuckets(parsed: ParsedContent, logger: Logger): void {
  logger.debug('assertRequiredBuckets:start');

  const missing: string[] = [];
  if (parsed.weapons.length === 0) missing.push('weapons');
  if (parsed.passives.length === 0) missing.push('passives');
  if (parsed.evolutionRecipes.length === 0) missing.push('evolutionRecipes');
  if (parsed.consumables.length === 0) missing.push('consumables');
  if (parsed.enemies.length === 0) missing.push('enemies');
  if (parsed.biomes.length === 0) missing.push('biomes');
  if (!parsed.waveTable) missing.push('waveTable');
  if (!parsed.gameSettings) missing.push('gameSettings');

  if (missing.length > 0) {
    logger.error('assertRequiredBuckets:missing-content', { missing });
    throw new Error(`Missing required content buckets: ${missing.join(', ')}`);
  }

  logger.debug('assertRequiredBuckets:complete');
}

function assertCrossReferences(parsed: Required<ParsedContent>, logger: Logger): void {
  logger.debug('assertCrossReferences:start');

  const weaponIds = new Set(parsed.weapons.map(weapon => weapon.id));
  const passiveIds = new Set(parsed.passives.map(passive => passive.id));
  const enemyIds = new Set(parsed.enemies.map(enemy => enemy.id));
  const consumableIds = new Set(parsed.consumables.map(consumable => consumable.id));
  const errors: string[] = [];

  for (const starterWeaponId of parsed.gameSettings.starterWeaponIds) {
    if (!weaponIds.has(starterWeaponId)) {
      errors.push(`Unknown gameSettings.starterWeaponIds entry: ${starterWeaponId}`);
    }
    if (!parsed.gameSettings.availableWeaponIds.includes(starterWeaponId)) {
      errors.push(`gameSettings.starterWeaponIds entry is not in availableWeaponIds: ${starterWeaponId}`);
    }
  }

  for (const availableWeaponId of parsed.gameSettings.availableWeaponIds) {
    if (!weaponIds.has(availableWeaponId)) {
      errors.push(`Unknown gameSettings.availableWeaponIds entry: ${availableWeaponId}`);
    }
  }

  assertBossEncounterSemantics(parsed.gameSettings, enemyIds, errors, logger);

  if (!enemyIds.has(parsed.gameSettings.bossEnemyId)) {
    errors.push(`Unknown gameSettings.bossEnemyId: ${parsed.gameSettings.bossEnemyId}`);
  }

  for (const consumableId of parsed.gameSettings.consumableFallbackIds) {
    if (!consumableIds.has(consumableId)) {
      errors.push(`Unknown gameSettings.consumableFallbackIds entry: ${consumableId}`);
    }
  }

  if (parsed.gameSettings.starterWeaponIds.length > parsed.gameSettings.maxWeapons) {
    errors.push(
      `gameSettings.starterWeaponIds count exceeds maxWeapons: ${parsed.gameSettings.starterWeaponIds.length}/${parsed.gameSettings.maxWeapons}`
    );
  }

  for (const recipe of parsed.evolutionRecipes) {
    if (!weaponIds.has(recipe.baseWeaponId)) errors.push(`Unknown evolution baseWeaponId: ${recipe.baseWeaponId}`);
    if (!weaponIds.has(recipe.evolvedWeaponId)) errors.push(`Unknown evolution evolvedWeaponId: ${recipe.evolvedWeaponId}`);
    if (!passiveIds.has(recipe.requiredPassiveId)) errors.push(`Unknown evolution requiredPassiveId: ${recipe.requiredPassiveId}`);
  }

  parsed.waveTable.forEach((wave, waveIndex) => {
    wave.spawns.forEach((spawn, spawnIndex) => {
      if (!enemyIds.has(spawn.enemyId)) {
        errors.push(`Unknown wave enemyId at wave ${waveIndex}, spawn ${spawnIndex}: ${spawn.enemyId}`);
      }

      for (const modifierId of spawn.modifiers) {
        if (!KNOWN_WAVE_MODIFIER_IDS.has(modifierId)) {
          errors.push(`Unknown wave modifier at wave ${waveIndex}, spawn ${spawnIndex}: ${modifierId}`);
        }
      }
    });
  });

  assertEnemyBehaviorSemantics(parsed.enemies, errors);
  assertBiomeSemantics(parsed.biomes, errors, logger);

  if (errors.length > 0) {
    logger.error('assertCrossReferences:invalid-content-references', { errors });
    throw new Error(`Invalid content references: ${errors.join('; ')}`);
  }

  assertEnemyDropSemantics(parsed.enemies, logger);

  logger.debug('assertCrossReferences:complete', {
    weapons: weaponIds.size,
    passives: passiveIds.size,
    enemies: enemyIds.size,
    consumables: consumableIds.size,
    biomes: parsed.biomes.length,
    waves: parsed.waveTable.length
  });
}

function assertBossEncounterSemantics(
  settings: GameSettingsContent,
  enemyIds: ReadonlySet<string>,
  errors: string[],
  logger: Logger
): void {
  const encounters = settings.bossEncounters ?? [];
  const victoryRequirements = settings.bossVictoryRequirements;

  logger.debug('assertBossEncounterSemantics:start', {
    encounterCount: encounters.length,
    victoryRequirements
  });

  if (encounters.length === 0) {
    errors.push('gameSettings.bossEncounters must contain at least one entry');
    return;
  }

  const encounterIds = new Set<string>();
  const encounterById = new Map<string, BossEncounterContent>();
  let lastSpawnTime = Number.NEGATIVE_INFINITY;

  encounters.forEach((encounter, index) => {
    if (encounterIds.has(encounter.id)) {
      errors.push(`Duplicate gameSettings.bossEncounters id: ${encounter.id}`);
    }
    encounterIds.add(encounter.id);
    encounterById.set(encounter.id, encounter);

    if (!enemyIds.has(encounter.enemyId)) {
      errors.push(`Unknown gameSettings.bossEncounters[${index}].enemyId: ${encounter.enemyId}`);
    }

    if (!Number.isFinite(encounter.spawnTime) || encounter.spawnTime <= 0) {
      errors.push(`Non-positive gameSettings.bossEncounters[${index}].spawnTime: ${encounter.spawnTime}`);
    }

    if (encounter.spawnTime <= lastSpawnTime) {
      errors.push(
        `Non-monotonic gameSettings.bossEncounters spawnTime at index ${index}: ${encounter.spawnTime} <= ${lastSpawnTime}`
      );
    }
    lastSpawnTime = encounter.spawnTime;

    assertBossEncounterStatModifierSemantics(encounter.statModifiers, index, errors);
    assertBossEncounterRewardPolicySemantics(encounter.rewardPolicy, index, errors);

    if (encounter.hpMultiplier !== undefined && (!Number.isFinite(encounter.hpMultiplier) || encounter.hpMultiplier <= 0)) {
      errors.push(`Malformed gameSettings.bossEncounters[${index}].hpMultiplier: ${encounter.hpMultiplier}`);
    }
  });

  if (!victoryRequirements) {
    errors.push('Missing gameSettings.bossVictoryRequirements');
    return;
  }

  const requiredEncounterIds = new Set(victoryRequirements.requiredEncounterIds);
  for (const encounterId of victoryRequirements.requiredEncounterIds) {
    const encounter = encounterById.get(encounterId);
    if (!encounter) {
      errors.push(`gameSettings.bossVictoryRequirements.requiredEncounterIds references missing encounter: ${encounterId}`);
      continue;
    }

    if (encounter.requiredForVictory === false) {
      errors.push(`gameSettings.bossVictoryRequirements.requiredEncounterIds references non-required encounter: ${encounterId}`);
    }
  }

  if (requiredEncounterIds.size !== victoryRequirements.requiredEncounterIds.length) {
    errors.push('Malformed gameSettings.bossVictoryRequirements.requiredEncounterIds: duplicate entries are not allowed');
  }

  if (!Number.isInteger(victoryRequirements.requiredBossCount) || victoryRequirements.requiredBossCount <= 0) {
    errors.push(`Malformed gameSettings.bossVictoryRequirements.requiredBossCount: ${victoryRequirements.requiredBossCount}`);
    return;
  }

  if (victoryRequirements.requiredBossCount > requiredEncounterIds.size) {
    logger.warn('assertBossEncounterSemantics:insufficient-required-encounter-ids', {
      requiredBossCount: victoryRequirements.requiredBossCount,
      requiredEncounterIds: victoryRequirements.requiredEncounterIds
    });
    errors.push(
      `gameSettings.bossVictoryRequirements.requiredBossCount (${victoryRequirements.requiredBossCount}) exceeds requiredEncounterIds (${requiredEncounterIds.size})`
    );
  }

  const availableRequiredEncounters = encounters.filter(encounter => encounter.requiredForVictory !== false).length;
  if (victoryRequirements.requiredBossCount > availableRequiredEncounters) {
    errors.push(
      `gameSettings.bossVictoryRequirements.requiredBossCount (${victoryRequirements.requiredBossCount}) exceeds available required encounters (${availableRequiredEncounters})`
    );
  }
}

function assertBossEncounterStatModifierSemantics(
  statModifiers: BossEncounterStatModifiersContent | undefined,
  encounterIndex: number,
  errors: string[]
): void {
  if (!statModifiers) {
    return;
  }

  const numericFields: Array<keyof BossEncounterStatModifiersContent> = ['damageMultiplier', 'speedMultiplier', 'xpRewardMultiplier'];
  const hasAtLeastOneModifier = numericFields.some(field => statModifiers[field] !== undefined);
  if (!hasAtLeastOneModifier) {
    errors.push(`Malformed gameSettings.bossEncounters[${encounterIndex}].statModifiers: at least one modifier is required`);
  }

  for (const field of numericFields) {
    const value = statModifiers[field];
    if (value === undefined) {
      continue;
    }

    if (!Number.isFinite(value) || value <= 0) {
      errors.push(`Malformed gameSettings.bossEncounters[${encounterIndex}].statModifiers.${field}: ${value}`);
    }
  }
}

function assertBossEncounterRewardPolicySemantics(
  rewardPolicy: BossEncounterRewardPolicyContent | undefined,
  encounterIndex: number,
  errors: string[]
): void {
  if (!rewardPolicy) {
    return;
  }

  if (rewardPolicy.mode === 'guaranteedChest') {
    if (!Number.isInteger(rewardPolicy.chestCount) || (rewardPolicy.chestCount ?? 0) <= 0) {
      errors.push(
        `Malformed gameSettings.bossEncounters[${encounterIndex}].rewardPolicy.chestCount: ${rewardPolicy.chestCount}`
      );
    }
  } else if (rewardPolicy.chestCount !== undefined) {
    errors.push(
      `Malformed gameSettings.bossEncounters[${encounterIndex}].rewardPolicy.chestCount: chestCount is only valid for mode=guaranteedChest`
    );
  }

  if (rewardPolicy.mode === 'guaranteedGems') {
    if (!Number.isFinite(rewardPolicy.gemMultiplier) || (rewardPolicy.gemMultiplier ?? 0) <= 0) {
      errors.push(
        `Malformed gameSettings.bossEncounters[${encounterIndex}].rewardPolicy.gemMultiplier: ${rewardPolicy.gemMultiplier}`
      );
    }
  } else if (rewardPolicy.gemMultiplier !== undefined) {
    errors.push(
      `Malformed gameSettings.bossEncounters[${encounterIndex}].rewardPolicy.gemMultiplier: gemMultiplier is only valid for mode=guaranteedGems`
    );
  }
}

function assertEnemyBehaviorSemantics(enemies: readonly EnemyContent[], errors: string[]): void {
  for (const enemy of enemies) {
    const params = enemy.behaviorParams;
    if (!params) {
      continue;
    }

    if (params.dashMinDistance !== undefined && params.dashMaxDistance !== undefined && params.dashMinDistance >= params.dashMaxDistance) {
      errors.push(
        `Invalid enemy behavior range for ${enemy.id}: dashMinDistance (${params.dashMinDistance}) must be < dashMaxDistance (${params.dashMaxDistance})`
      );
    }

    if (
      params.rangedRetreatDistance !== undefined &&
      params.rangedPreferredDistance !== undefined &&
      params.rangedRetreatDistance >= params.rangedPreferredDistance
    ) {
      errors.push(
        `Invalid enemy behavior range for ${enemy.id}: rangedRetreatDistance (${params.rangedRetreatDistance}) must be < rangedPreferredDistance (${params.rangedPreferredDistance})`
      );
    }
  }
}

function assertBiomeSemantics(biomes: readonly BiomeContent[], errors: string[], logger: Logger): void {
  logger.debug('assertBiomeSemantics:start', { biomes: biomes.length });

  const baselineBiomes = biomes.filter(biome => (biome.distanceThreshold ?? 0) === 0);
  if (baselineBiomes.length !== 1) {
    errors.push(
      `Biome content must define exactly one baseline distanceThreshold 0 biome: found ${baselineBiomes.length}`
    );
  }

  logger.debug('assertBiomeSemantics:complete', {
    baselineBiomeIds: baselineBiomes.map(biome => biome.id)
  });
}

function assertEnemyDropSemantics(enemies: readonly EnemyContent[], logger: Logger): void {
  logger.debug('assertEnemyDropSemantics:start', { enemies: enemies.length });

  for (const enemy of enemies) {
    if (!enemy.drops || enemy.drops.length === 0) {
      logger.debug('assertEnemyDropSemantics:enemy-uses-default-gem-fallback', {
        enemyId: enemy.id,
        fallbackGemValue: enemy.stats.xpReward
      });
      logger.debug('assertEnemyDropSemantics:resolved-drop-table', {
        enemyId: enemy.id,
        drops: [{ type: 'gem', chance: 1, value: enemy.stats.xpReward, source: 'enemy.stats.xpReward' }]
      });
      continue;
    }

    const resolvedDrops = enemy.drops.map(drop => {
      if (drop.type === 'gem') {
        return {
          type: drop.type,
          chance: drop.chance,
          value: drop.value ?? enemy.stats.xpReward,
          source: drop.value === undefined ? 'enemy.stats.xpReward' : 'drop.value'
        };
      }

      if (drop.value !== undefined) {
        logger.warn('assertEnemyDropSemantics:chest-value-ignored', {
          enemyId: enemy.id,
          value: drop.value
        });
      }

      return {
        type: drop.type,
        chance: drop.chance,
        source: 'drop.chance'
      };
    });

    logger.debug('assertEnemyDropSemantics:resolved-drop-table', {
      enemyId: enemy.id,
      drops: resolvedDrops
    });
  }

  logger.debug('assertEnemyDropSemantics:complete');
}

function assertWeaponConsistency(entry: ContentManifestEntry, weapon: WeaponContent, logger: Logger): void {
  logger.debug('assertWeaponConsistency:start', {
    weaponId: weapon.id,
    behavior: weapon.type,
    path: entry.path
  });

  const errors: string[] = [];
  const levels = weapon.levels.map(level => level.level);
  const uniqueLevels = new Set<number>();

  for (const level of levels) {
    if (uniqueLevels.has(level)) {
      errors.push(`Duplicate weapon level definition: ${weapon.id} level ${level}`);
    }
    uniqueLevels.add(level);
  }

  if (!uniqueLevels.has(1)) {
    errors.push(`Missing required weapon level 1 definition: ${weapon.id}`);
  }

  const maxLevel = Math.max(...levels);
  for (let expectedLevel = 1; expectedLevel <= maxLevel; expectedLevel += 1) {
    if (!uniqueLevels.has(expectedLevel)) {
      errors.push(`Missing weapon level definition: ${weapon.id} level ${expectedLevel}`);
    }
  }

  for (const level of weapon.levels) {
    assertBehaviorLevelConsistency(weapon, level.level, entry.path, errors, logger);
  }

  if (errors.length > 0) {
    logger.error('assertWeaponConsistency:invalid-weapon-content', {
      weaponId: weapon.id,
      behavior: weapon.type,
      path: entry.path,
      errors
    });
    throw new Error(`Invalid weapon consistency for ${weapon.id}: ${errors.join('; ')}`);
  }

  logger.debug('assertWeaponConsistency:complete', {
    weaponId: weapon.id,
    behavior: weapon.type,
    levels: weapon.levels.length
  });
}

function assertBehaviorLevelConsistency(
  weapon: WeaponContent,
  level: number,
  path: string,
  errors: string[],
  logger: Logger
): void {
  const stats = resolveWeaponStatsAtLevel(weapon, level);

  switch (weapon.type) {
    case 'beam': {
      if (!stats.range || stats.range <= 0) {
        errors.push(`Beam weapon requires positive range: ${weapon.id} level ${level} (${path})`);
      }
      break;
    }
    case 'boomerang': {
      if (!stats.returnSpeed || stats.returnSpeed <= 0) {
        errors.push(`Boomerang weapon requires positive returnSpeed: ${weapon.id} level ${level} (${path})`);
      }
      if (!stats.life || stats.life <= 0) {
        errors.push(`Boomerang weapon requires positive life: ${weapon.id} level ${level} (${path})`);
      }
      break;
    }
    case 'chain': {
      const chainHit = stats.chainHit ?? stats.modifiers?.chainHit;
      const maxTargets = stats.modifiers?.chainLightning?.maxTargets;
      if ((!chainHit || chainHit <= 0) && (!maxTargets || maxTargets <= 0)) {
        errors.push(`Chain weapon requires positive chainHit or chainLightning.maxTargets: ${weapon.id} level ${level} (${path})`);
      }

      if ((!chainHit || chainHit <= 0) && maxTargets && maxTargets > 0) {
        logger.warn('assertWeaponConsistency:chain-hit-default-applied', {
          weaponId: weapon.id,
          level,
          path,
          fallbackChainHit: maxTargets
        });
      }
      break;
    }
    default:
      assertAreaWeaponConsistency(weapon.type, weapon.id, level, path, stats.area, errors);
  }
}

function assertAreaWeaponConsistency(
  behavior: WeaponBehaviorType,
  weaponId: string,
  level: number,
  path: string,
  area: number | undefined,
  errors: string[]
): void {
  if (behavior === 'area' && (!area || area <= 0)) {
    errors.push(`Area weapon requires positive area: ${weaponId} level ${level} (${path})`);
  }
}

function resolveWeaponStatsAtLevel(weapon: WeaponContent, level: number): WeaponContent['baseStats'] {
  const levelConfig = weapon.levels.find(item => item.level === level);
  if (!levelConfig) {
    return weapon.baseStats;
  }

  return { ...weapon.baseStats, ...levelConfig.projectile };
}
